import { patternMetadata, getPatternMetadataAlphabetically } from "./PatternMetadata";

/**
 * Pattern loaders - lazy load pattern definitions on demand
 * Each loader returns a promise that resolves to the pattern array
 */
const patternLoaders = {
  combos: () => import("./patterns/combos").then((m) => m.comboPatterns),
  numerical: () => import("./patterns/numerical").then((m) => m.numericalPatterns),
  a: () => import("./patterns/a").then((m) => m.aPatterns),
  b: () => import("./patterns/b").then((m) => m.bPatterns),
  c: () => import("./patterns/c").then((m) => m.cPatterns),
  d: () => import("./patterns/d").then((m) => m.dPatterns),
  e: () => import("./patterns/e").then((m) => m.ePatterns),
  f: () => import("./patterns/f").then((m) => m.fPatterns),
  g: () => import("./patterns/g").then((m) => m.gPatterns),
  h: () => import("./patterns/h").then((m) => m.hPatterns),
  i: () => import("./patterns/i").then((m) => m.iPatterns),
  j: () => import("./patterns/j").then((m) => m.jPatterns),
  k: () => import("./patterns/k").then((m) => m.kPatterns),
  l: () => import("./patterns/l").then((m) => m.lPatterns),
  m: () => import("./patterns/m").then((m) => m.mPatterns),
  n: () => import("./patterns/n").then((m) => m.nPatterns),
  o: () => import("./patterns/o").then((m) => m.oPatterns),
  p: () => import("./patterns/p").then((m) => m.pPatterns),
  q: () => import("./patterns/q").then((m) => m.qPatterns),
  r: () => import("./patterns/r").then((m) => m.rPatterns),
  s: () => import("./patterns/s").then((m) => m.sPatterns),
  t: () => import("./patterns/t").then((m) => m.tPatterns),
  u: () => import("./patterns/u").then((m) => m.uPatterns),
  v: () => import("./patterns/v").then((m) => m.vPatterns),
  w: () => import("./patterns/w").then((m) => m.wPatterns),
  x: () => import("./patterns/x").then((m) => m.xPatterns),
  y: () => import("./patterns/y").then((m) => m.yPatterns),
  z: () => import("./patterns/z").then((m) => m.zPatterns),
  new: () => import("./patterns/new").then((m) => m.newPatterns),
};

// Cache for loaded pattern definitions (not metadata)
const patternDefinitionCache = new Map();

/**
 * Get pattern metadata only (lightweight, synchronous)
 * Use this for dropdowns, search, filtering
 * @returns {Array} Array of pattern metadata objects
 */
export function getPresetPatternsMetadata() {
  return getPatternMetadataAlphabetically();
}

/**
 * Load full pattern definition by value
 * @param {String} patternValue - The pattern's value (e.g., "X", "Four Corners")
 * @returns {Promise<Object>} Full pattern object with grid data
 */
export async function loadPatternDefinition(patternValue) {
  // Handle blank/custom patterns
  if (patternValue === "blank" || patternValue === "Select One") {
    return getBlankPattern();
  }

  if (patternValue === "Custom") {
    // Return custom pattern template
    return {
      value: "Custom",
      label: "Custom",
      otherNames: [],
      pattern: {
        B: [false, false, false, false, false],
        I: [false, false, false, false, false],
        N: [false, false, false, false, false],
        G: [false, false, false, false, false],
        O: [false, false, false, false, false],
      },
      unusedLetters: [],
      credit: null,
    };
  }

  // Check cache first
  if (patternDefinitionCache.has(patternValue)) {
    return patternDefinitionCache.get(patternValue);
  }

  // Find the metadata to know which file to load
  const meta = patternMetadata.find((p) => p.value === patternValue);

  if (!meta || !meta.source) {
    console.warn(`Pattern "${patternValue}" not found in metadata`);
    return getBlankPattern();
  }

  // Load the pattern file
  const loader = patternLoaders[meta.source];

  if (!loader) {
    console.warn(`No loader found for source "${meta.source}"`);
    return getBlankPattern();
  }

  try {
    const patterns = await loader();
    const pattern = patterns.find((p) => p.value === patternValue);

    if (pattern) {
      // Cache the full pattern definition
      patternDefinitionCache.set(patternValue, pattern);
      return pattern;
    } else {
      console.warn(`Pattern "${patternValue}" not found in ${meta.source}.js`);
      return getBlankPattern();
    }
  } catch (error) {
    console.error(`Error loading pattern "${patternValue}":`, error);
    return getBlankPattern();
  }
}

/**
 * Legacy function - loads ALL patterns
 * ⚠️ ONLY use this on the /patterns page where you need all patterns
 * This defeats the lazy loading optimization!
 *
 * @returns {Promise<Array>} Array of all pattern objects with full data
 */
export async function getPresetPatterns() {
  try {
    // Load pattern files in specific order: new first, then combos, numerical, a-z
    const loadOrder = [
      "new", // NEW patterns always first!
      "combos",
      "numerical",
      "a",
      "b",
      "c",
      "d",
      "e",
      "f",
      "g",
      "h",
      "i",
      "j",
      "k",
      "l",
      "m",
      "n",
      "o",
      "p",
      "q",
      "r",
      "s",
      "t",
      "u",
      "v",
      "w",
      "x",
      "y",
      "z",
    ];

    // Load all patterns in parallel (order preserved)
    const loaderPromises = loadOrder.map((key) => patternLoaders[key]());
    const allPatternArrays = await Promise.all(loaderPromises);

    // Flatten and combine all patterns (order is preserved)
    const allPatterns = [].concat(...allPatternArrays);

    // Add custom pattern at the beginning
    return [
      {
        value: "Custom",
        label: "Custom",
        otherNames: [],
        pattern: {
          B: [false, false, false, false, false],
          I: [false, false, false, false, false],
          N: [false, false, false, false, false],
          G: [false, false, false, false, false],
          O: [false, false, false, false, false],
        },
        unusedLetters: [],
        credit: null,
      },
      ...allPatterns,
    ];
  } catch (error) {
    console.error("Error loading all patterns:", error);
    return [getBlankPattern()];
  }
}

/**
 * Legacy function - loads all patterns and sorts alphabetically
 * ⚠️ ONLY use on /patterns page
 *
 * @returns {Promise<Array>} Sorted array of all patterns
 */
export async function getPresetPatternsAlphabetically() {
  const presets = await getPresetPatterns();
  return presets.sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Get pattern info by name (async version)
 * If pattern doesn't exist, returns blank pattern
 *
 * @param {String} patternName - Pattern value to find
 * @returns {Promise<Object>} Pattern object with full data
 */
export async function getPatternInfo(patternName) {
  return await loadPatternDefinition(patternName);
}

/**
 * Returns a default blank pattern
 * @returns {Object} Blank pattern object
 */
export function getBlankPattern() {
  return {
    value: "blank",
    label: "Select One",
    otherNames: [],
    pattern: {
      B: [false, false, false, false, false],
      I: [false, false, false, false, false],
      N: [false, false, false, false, false],
      G: [false, false, false, false, false],
      O: [false, false, false, false, false],
    },
    unusedLetters: [],
    credit: null,
  };
}
